import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { ManagerActions } from './manager.actions';
import { Transaction } from '../../features/home/models/transaction.model';
import { ManagerService } from '../../features/manager/services/manager.service';
import { QualityTrafficLights } from '../../features/shared/models/common/quality-traffic-lights.enum';
import { RootState } from '../root-state.model';
import { TransactionSelectors } from '../transaction/transaction.selectors';

@Injectable()
export class ManagerEffects {
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly service = inject(ManagerService);

  calculateListEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagerActions.CalculateList),
      withLatestFrom(this.store, this.store.select(TransactionSelectors.isLatestIssuanceLookup)),
      map(([action, store, isLatestIssuanceLookup]) => {
        try {
          if (store.manager.trafficLightsCalculated) {
            return ManagerActions.CalculateListSuccess({ list: store.manager.list });
          }

          // Gather transactions by managerId
          const transactionsByManagerId: Record<string, Transaction[]> = {};
          for (const transaction of store.transaction.list) {
            const managerId = transaction.managerId;
            if (!transactionsByManagerId[managerId]) {
              transactionsByManagerId[managerId] = [];
            }
            transactionsByManagerId[managerId].push(transaction);
          }

          // Iterate over managers
          const list = store.manager.list?.map((manager) => {
            const transactionsByManager = transactionsByManagerId[manager.managerId] || [];

            const qualities = Object.values(QualityTrafficLights);
            const transactionQualities = {} as Record<QualityTrafficLights, number>;
            const transactionQualitiesLatestIssuance = {} as Record<QualityTrafficLights, number>;

            // Initialize counts to zero
            for (const quality of qualities) {
              transactionQualities[quality] = 0;
              transactionQualitiesLatestIssuance[quality] = 0;
            }

            // Iteration over transactions
            for (const t of transactionsByManager) {
              if (t.assetShortName === manager.assetShortName) {
                if (t.endOfLife) {
                  continue;
                }

                const quality = t.qualityTrafficLight;
                transactionQualities[quality] += 1;

                if (!!t.transactionNameRoot && isLatestIssuanceLookup[t.transactionId]) {
                  transactionQualitiesLatestIssuance[quality] += 1;
                }
              }
            }

            return {
              ...manager,
              transactionQualities,
              transactionQualitiesLatestIssuance,
            };
          });

          return ManagerActions.CalculateListSuccess({ list });
        } catch (error) {
          return ManagerActions.CalculateListFail({ error });
        }
      })
    )
  );

  loadListEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagerActions.LoadList),
      switchMap((action) =>
        this.service.getAll().pipe(
          map((managers) => ManagerActions.LoadListSuccess({ list: managers })),
          catchError((error) => of(ManagerActions.LoadListFail({ error })))
        )
      )
    )
  );
}
